import './KpiMapping.scss';
import { useEffect, useMemo, useState } from 'react';
import { KpiMappings, AccountInfo } from '@blisspointmedia/bpm-types/dist/KpiMapping';
import { format, subMonths, subDays } from 'date-fns';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { FullPageSpinner } from '../../Components/Spinner';
import { Page } from '../../Components/Page';
import { DatePicker } from '../../Components/DatePicker';
import { DateRange } from '../../Components/DatePicker/date-picker-types';
import { EditKpiMapping } from './EditKpiMapping';
import { useSetError } from '../../redux/modals';
import { awaitJSON, CrossChannelLambdaFetch } from '../../utils/fetch-utils';
import { KpiFunnels } from './KpiFunnels';
import { KpiFunnel, KpiMappingsContext } from './kpi-mapping-types';
import { Nav, NAV_LABELS, NavType } from './kpi-mapping-constants';

const NAVS = [
  { label: NAV_LABELS[Nav.KPI_MAPPING], key: Nav.KPI_MAPPING },
  { label: NAV_LABELS[Nav.CROSS_CHANNEL_FUNNEL], key: Nav.CROSS_CHANNEL_FUNNEL }
];

const DATE_FORMAT = 'yyyy-MM-dd';

const defaultDates = (): DateRange => {
  const now = new Date();
  const start = format(subMonths(now, 1), DATE_FORMAT);
  const end = format(subDays(now, 1), DATE_FORMAT);
  return { start, end };
};

export const KpiMapping = () => {
  const { cross_channel_kpis: crossChannelKpis, cid: company } = useCompanyInfo();
  const setError = useSetError();
  const [editMode, setEditMode] = useState(false);
  const [kpiMappings, setKpiMappings] = useState<KpiMappings | undefined>();
  const [kpiOptions, setKpiOptions] = useState<Record<string, string[]>>();
  const [sourceIdMap, setSourceIdMap] = useState<Record<string, number>>();
  const [sourceToPrettyName, setSourceToPrettyName] = useState<Record<string, string>>();
  const [accountInfoBySource, setAccountInfoBySource] = useState<Record<string, AccountInfo[]>>();
  const [selectedNav, setSelectedNav] = useState<NavType>(Nav.KPI_MAPPING);
  const [kpiFunnels, setKpiFunnels] = useState<KpiFunnel[]>();
  const [rawSourceKpiCounts, setRawSourceKpiCounts] = useState({});
  const [kpiCountsLoading, setKpiCountsLoading] = useState(false);
  const [dates, setDates] = useState<DateRange | undefined>(defaultDates);
  const [lags, setLags] = useState<Record<string, string>>({});

  const yesterday = useMemo(() => format(subDays(new Date(), 1), DATE_FORMAT), []);

  useEffect(() => {
    if (!company || kpiMappings) {
      return;
    }
    (async () => {
      try {
        const res = await CrossChannelLambdaFetch('/getKpiMappings', {
          params: {
            company
          }
        });
        const data = await res.json();
        setKpiMappings(data.kpiMappings || {});
        setSourceIdMap(data.sourceIdMap || {});
        setSourceToPrettyName(data.sourceToPrettyName || {});
        setAccountInfoBySource(data.accountInfoBySource || {});
        setLags(data.lags || {});
      } catch (e) {
        setError({
          message: `Failed to fetch KPI mapping data for ${company}. Error: ${e.message}`,
          reportError: e
        });
      }
    })();
  }, [company, kpiMappings, setError]);

  useEffect(() => {
    if (!company) {
      return;
    }
    (async () => {
      try {
        const res = await CrossChannelLambdaFetch('/getKpiOptions', {
          params: {
            company
          }
        });
        const data = await res.json();
        setKpiOptions(data || {});
      } catch (e) {
        setError({
          message: `Failed to fetch source KPI options for ${company}. Error: ${e.message}`,
          reportError: e
        });
      }
    })();
  }, [company, setError]);

  useEffect(() => {
    if (!company || kpiFunnels) {
      return;
    }
    (async () => {
      try {
        const res = await CrossChannelLambdaFetch('/getKpiFunnels', {
          params: {
            company
          }
        });
        const data = await res.json();
        setKpiFunnels(data || []);
      } catch (e) {
        setError({
          message: `Failed to fetch KPI Funnels for ${company}. Error: ${e.message}`,
          reportError: e
        });
      }
    })();
  }, [company, kpiFunnels, setError]);

  useEffect(() => {
    if (!company || !dates) {
      return;
    }
    (async () => {
      try {
        setKpiCountsLoading(true);
        const res = await CrossChannelLambdaFetch('/getRawSourceKpiCounts', {
          params: {
            company,
            start: dates.start,
            end: dates.end
          }
        });
        const data = await awaitJSON(res);
        setRawSourceKpiCounts(data || {});
      } catch (e) {
        setError({
          message: `Failed to fetch raw source KPI counts for ${company}. Error: ${e.message}`,
          reportError: e
        });
      } finally {
        setKpiCountsLoading(false);
      }
    })();
  }, [company, dates, setError]);

  const sourcesWithKpis = useMemo(
    () => (kpiOptions ? Object.keys(kpiOptions).sort() : []),
    [kpiOptions]
  );

  const contextValue = useMemo(
    () => ({
      sourceIdMap: sourceIdMap || {},
      kpiOptions: kpiOptions!,
      kpiMappings: kpiMappings!,
      sourcesWithKpis,
      sourceToPrettyName: sourceToPrettyName!,
      accountInfoBySource: accountInfoBySource || {},
      rawSourceKpiCounts: rawSourceKpiCounts || {},
      kpiCountsLoading,
      lags: lags || {},
      editMode
    }),
    [
      sourceIdMap,
      kpiOptions,
      kpiMappings,
      sourcesWithKpis,
      sourceToPrettyName,
      accountInfoBySource,
      rawSourceKpiCounts,
      kpiCountsLoading,
      lags,
      editMode
    ]
  );

  return (
    <Page
      title='KPI Mapping'
      pageType='KPI Mapping'
      navs={NAVS}
      selectedNav={selectedNav}
      onNav={val => {
        setSelectedNav(val as NavType);
        setEditMode(false);
      }}
      actions={
        <DatePicker
          range={dates}
          isOutsideRange={date => date > yesterday}
          onChange={({ start, end }) => {
            setDates({ start, end });
          }}
        />
      }
    >
      {selectedNav === Nav.CROSS_CHANNEL_FUNNEL ? (
        <KpiFunnels
          funnels={kpiFunnels}
          crossChannelKpis={crossChannelKpis || []}
          company={company}
          setKpiFunnels={setKpiFunnels}
        />
      ) : (
        <div className='kpi-mapping-page'>
          {kpiMappings && kpiOptions && sourcesWithKpis && sourceToPrettyName ? (
            <KpiMappingsContext.Provider value={contextValue}>
              <EditKpiMapping
                company={company}
                data={kpiMappings}
                setEditMode={setEditMode}
                setKpiMappings={setKpiMappings}
              />
            </KpiMappingsContext.Provider>
          ) : (
            <FullPageSpinner />
          )}
        </div>
      )}
    </Page>
  );
};
